import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import {
  createAdminExport,
  downloadAdminExport,
  getAdminDashboardSummary,
  refreshAdminStats,
  getAdminSessionStatus,
  unlockAdminDashboard,
  lockAdminDashboard,
  clearAdminSessionToken,
} from '../services/adminDashboardService';
import AssistantNotificationAdmin from '../components/AssistantNotificationAdmin';
import AdminOperationsOverview from '../components/AdminOperationsOverview';
import PerformanceMonitoringAdmin from '../components/PerformanceMonitoringAdmin';
import EngagementAdminPanel from '../components/EngagementAdminPanel';
import FeedbackAdminPanel from '../components/FeedbackAdminPanel';
import AccountDeletionSurveyAdminPanel from '../components/AccountDeletionSurveyAdminPanel';
import LiveUserActivityAdmin from '../components/LiveUserActivityAdmin';
import CorporateTrafficAdmin from '../components/CorporateTrafficAdmin';
import EmailSystemAdmin from '../components/EmailSystemAdmin';
import { getPremiumConfig, savePremiumConfig } from '../services/premiumService';
import { tumUygulamaKullanicilariniGetir, tumKullanicilaraSistemDuyurusuGonder } from '../services/adminNotificationService';
import { bildirimGecmisiniGetir } from '../services/notificationHistoryService';
import {
  otomatikBildirimleriSimdiCalistir,
  otomatikBildirimLoglariniGetir,
} from '../services/automationRulesService';
import {
  bildirimPolitikalariGetir,
  bildirimPolitikasiKaydet,
  bildirimPolitikalariVarsayilanaKur,
} from '../services/notificationPolicyService';
import '../styles/pages/adminDashboard.css';

const fmt = (value) => Number(value || 0).toLocaleString('tr-TR');
const bytes = (value) => {
  const n = Number(value || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};
const n = (value) => Number(value || 0);
const AdminTrendRefreshContext = React.createContext({ refreshSignal: 0, trends: {} });

function sanitizeSummary(raw = {}) {
  const users = { ...(raw.users || {}) };
  const total = n(users.total);
  users.tr = Math.min(n(users.tr), total);
  users.en = Math.min(n(users.en), total);
  users.emailVerified = Math.min(n(users.emailVerified), total);
  users.emailUnverified = Math.max(total - users.emailVerified, 0);
  return { ...raw, users };
}


function flattenTrendNumbers(value, prefix = '', out = {}) {
  if (value === null || value === undefined) return out;
  if (typeof value === 'number') {
    if (Number.isFinite(value)) out[prefix] = value;
    return out;
  }
  if (typeof value === 'string') {
    const parsed = parseTrendNumber(value);
    if (parsed !== null) out[prefix] = parsed;
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item, idx) => {
      const stableKey = item && typeof item === 'object' && (item.module || item.key || item.id || item.title || item.label)
        ? String(item.module || item.key || item.id || item.title || item.label)
        : String(idx);
      flattenTrendNumbers(item, `${prefix}.${stableKey}`, out);
    });
    return out;
  }
  if (typeof value === 'object') {
    Object.keys(value).forEach((key) => flattenTrendNumbers(value[key], prefix ? `${prefix}.${key}` : key, out));
  }
  return out;
}

function buildTrendMap(previousSummary, nextSummary) {
  const prev = flattenTrendNumbers(previousSummary || {});
  const next = flattenTrendNumbers(nextSummary || {});
  const map = {};
  Object.keys(next).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(prev, key)) return;
    const delta = next[key] - prev[key];
    if (delta > 0) map[key] = 'up';
    else if (delta < 0) map[key] = 'down';
  });
  return map;
}

function parseTrendNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || /[A-Za-zÀ-ÿ]/.test(text.replace(/MB|KB|GB|B/g, ''))) return null;
  const numericText = text
    .replace(/%/g, '')
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const match = numericText.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function useNumberTrend(value, trendKey) {
  const trendContext = useContext(AdminTrendRefreshContext) || {};
  const refreshSignal = Number(trendContext.refreshSignal || 0);
  const forcedTrend = trendKey ? trendContext.trends?.[trendKey] : null;
  const previousRef = useRef(null);
  const [trend, setTrend] = useState(null);
  const numericValue = parseTrendNumber(value);

  useEffect(() => {
    if (forcedTrend) {
      setTrend({ direction: forcedTrend });
      previousRef.current = numericValue;
      return;
    }

    if (numericValue === null) {
      previousRef.current = null;
      setTrend(null);
      return;
    }

    const previous = previousRef.current;
    if (previous === null) {
      previousRef.current = numericValue;
      setTrend(null);
      return;
    }

    if (previous !== numericValue) {
      const delta = numericValue - previous;
      setTrend({ direction: delta > 0 ? 'up' : 'down' });
    } else {
      setTrend(null);
    }

    previousRef.current = numericValue;
  }, [numericValue, refreshSignal, forcedTrend]);

  return trend;
}

function TrendValue({ value, compact = false, trendKey }) {
  const trend = useNumberTrend(value, trendKey);
  const trendClass = trend?.direction === 'up' ? 'is-up' : trend?.direction === 'down' ? 'is-down' : '';
  return (
    <strong className={`adminTrendValue ${trendClass} ${compact ? 'compact' : ''}`}>
      <span>{value}</span>
    </strong>
  );
}
function StatPill({ label, value, hint, trendKey }) {
  return (
    <div className="adminStatPill">
      <TrendValue value={value} compact trendKey={trendKey || `pill.${label}`} />
      <span>{label}</span>
      {hint && <em>{hint}</em>}
    </div>
  );
}

function StatLine({ label, value, trendKey }) {
  return (
    <div className="adminStatLine">
      <span>{label}</span>
      <TrendValue value={value} trendKey={trendKey || `line.${label}`} />
    </div>
  );
}

function HealthChip({ label, ok = true }) {
  return (
    <div className={`adminHealthChip ${ok ? 'ok' : 'warn'}`}>
      <i />
      <span>{label}</span>
    </div>
  );
}

function AdminCard({ icon, title, subtitle, stats = [], actionLabel, onClick, accent = 'indigo', active = false }) {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component
      className={`adminDashCard accent-${accent} ${onClick ? 'clickable' : ''} ${active ? 'is-active' : ''}`}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="adminDashCardTop">
        <div className="adminDashIcon">{icon}</div>
        <div className="adminDashTitleBlock">
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {!!stats.length && (
        <div className="adminDashMiniStats">
          {stats.map((s) => <StatLine key={s.label} label={s.label} value={s.value} trendKey={s.trendKey || `card.${title}.${s.label}`} />)}
        </div>
      )}
      {actionLabel && (
        <div className="adminDashAction">
          <span>{actionLabel}</span>
          <i aria-hidden="true">{active ? '✓' : '›'}</i>
        </div>
      )}
    </Component>
  );
}


const ENGAGEMENT_MODULE_LABELS = {
  family_setup: ['Aile kurulumu', 'Family setup'],
  family_invite: ['Üye daveti', 'Member invite'],
  shopping: ['Alışveriş', 'Shopping'],
  calendar: ['Takvim', 'Calendar'],
  finance: ['Finans', 'Finance'],
  expenses: ['Harcamalar', 'Expenses'],
  tasks: ['Görevler', 'Tasks'],
  health: ['Sağlık', 'Health'],
  map: ['Aile haritası', 'Family map'],
  children: ['Çocuk takibi', 'Children'],
  chat: ['Aile sohbeti', 'Family chat'],
  documents: ['Belgeler', 'Documents'],
  cooking: ['Bugün ne pişirsem', 'Cooking'],
};

function moduleLabel(key, tr) {
  const pair = ENGAGEMENT_MODULE_LABELS[key] || [key, key];
  return tr ? pair[0] : pair[1];
}

function ratio(part, total) {
  const p = Number(part || 0);
  const t = Number(total || 0);
  if (!t) return '0%';
  return `${Math.round((p / t) * 100)}%`;
}

function EngagementStatsPanel({ tr, engagement = {} }) {
  const modules = Array.isArray(engagement.moduleBreakdown) ? engagement.moduleBreakdown : [];
  const totalUsers = Number(engagement.totalUsers || engagement.trackedUsers || 0);
  const topModules = modules.slice().sort((a, b) => Number((b.views || 0) + (b.dataAdds || 0)) - Number((a.views || 0) + (a.dataAdds || 0))).slice(0, 8);
  const weakModules = modules.slice().sort((a, b) => Number(b.usersWithoutData || 0) - Number(a.usersWithoutData || 0)).slice(0, 8);
  const actualModulesWithData = modules.filter((m) => Number(m.actualDataUsers ?? m.dataUsers ?? 0) > 0 || Number(m.actualDataRecords ?? m.dataAdds ?? 0) > 0).length;
  const actualNoDataModuleRows = modules.filter((m) => Number(m.actualDataUsers ?? m.dataUsers ?? 0) <= 0 && Number(m.actualDataRecords ?? m.dataAdds ?? 0) <= 0).length;
  return (
    <div className="adminEngagementStatsSection">
      <div className="adminSubSectionHeader">
        <div>
          <span>{tr ? 'Detaylı İstatistikler / Alt bölüm' : 'Detailed Statistics / Subsection'}</span>
          <h3>{tr ? 'Kullanıcı Hatırlatma Analizi' : 'User Reminder Analytics'}</h3>
          <p>{tr ? 'Bu kartlar kullanıcı hatırlatma sisteminin gerçek kullanıcı, push, modül ve gönderim sağlığını gösterir. Verileri güncelle dediğinde bu bölüm de yenilenir.' : 'These cards show real user, push, module and delivery health for the reminder system. Refresh updates this section too.'}</p>
        </div>
      </div>

      <div className="adminEngagementCardGrid">
        <AdminCard accent="blue" icon="👥" title={tr ? 'Kullanıcı Özeti' : 'User Summary'} stats={[
          { label: tr ? 'Toplam kullanıcı' : 'Total users', value: fmt(totalUsers) },
          { label: tr ? 'Engagement kaydı olan' : 'With engagement row', value: fmt(engagement.engagementTrackedUsers) },
          { label: tr ? 'Bugün aktif' : 'Active today', value: fmt(engagement.active24h) },
          { label: tr ? 'Son 7 gün aktif' : 'Active 7d', value: fmt(engagement.active7d) },
          { label: tr ? 'Son 30 gün aktif' : 'Active 30d', value: fmt(engagement.active30d) },
          { label: tr ? '10 gün pasif' : 'Inactive 10d', value: fmt(engagement.inactive10d) },
          { label: tr ? '30 gün pasif' : 'Inactive 30d', value: fmt(engagement.inactive30d) },
        ]} />
        <AdminCard accent="green" icon="🎯" title={tr ? 'Engagement' : 'Engagement'} stats={[
          { label: tr ? 'Modül takip satırı' : 'Module rows', value: fmt(engagement.moduleRows) },
          { label: tr ? 'Modül takipli kullanıcı' : 'Module-tracked users', value: fmt(engagement.moduleTrackedUsers) },
          { label: tr ? 'Toplam sayfa görüntüleme' : 'Total page views', value: fmt(engagement.totalViews) },
          { label: tr ? 'Toplam veri ekleme' : 'Total data adds', value: fmt(engagement.totalDataAdds) },
          { label: tr ? 'Veri ekleyen kullanıcı' : 'Users with data', value: fmt(engagement.usersWithAnyData) },
          { label: tr ? 'Sayfaya girmiş, veri yok' : 'Viewed, no data', value: fmt(engagement.usersViewedNoData) },
          { label: tr ? 'İlk veriye ort. saat' : 'Avg hours to first data', value: fmt(engagement.avgHoursToFirstData) },
        ]} />
        <AdminCard accent="indigo" icon="🔔" title={tr ? 'Push & İzin' : 'Push & Permission'} stats={[
          { label: tr ? 'Aktif push kullanıcısı' : 'Active push users', value: fmt(engagement.pushTokenUsers) },
          { label: tr ? 'Aktif push cihazı' : 'Active push devices', value: fmt(engagement.pushDeviceTotal) },
          { label: tr ? 'Push izni / token' : 'Push allowed/token', value: fmt(engagement.pushGranted) },
          { label: tr ? 'Push reddeden' : 'Push denied', value: fmt(engagement.pushDenied) },
          { label: tr ? 'Push bilinmeyen' : 'Push unknown', value: fmt(engagement.pushUnknown) },
          { label: tr ? 'Push kapsama' : 'Push coverage', value: ratio(engagement.pushGranted, totalUsers) },
        ]} />
        <AdminCard accent="amber" icon="🏠" title={tr ? 'Aile & Davet' : 'Family & Invite'} stats={[
          { label: tr ? 'Aile kurmamış kullanıcı' : 'Users without family', value: fmt(engagement.familySetupMissing) },
          { label: tr ? 'Tek üyeli aile' : 'Single-member families', value: fmt(engagement.familyInviteMissing) },
          { label: tr ? 'Kayıt olup neredeyse hiç açmayan' : 'Registered, barely opened', value: fmt(engagement.neverSeenAfterRegister) },
        ]} />
        <AdminCard accent="rose" icon="📨" title={tr ? 'Hatırlatma Gönderimleri' : 'Reminder Deliveries'} stats={[
          { label: tr ? 'Bugün gönderilen' : 'Sent today', value: fmt(engagement.sentToday) },
          { label: tr ? '7 gün gönderilen' : 'Sent 7d', value: fmt(engagement.sent7d) },
          { label: tr ? '30 gün gönderilen' : 'Sent 30d', value: fmt(engagement.sent30d) },
          { label: tr ? '7g atlanan deneme' : 'Skipped attempts 7d', value: fmt(engagement.skippedAttempts7d) },
          { label: tr ? '7g atlanan kullanıcı' : 'Skipped users 7d', value: fmt(engagement.uniqueSkippedUsers7d) },
          { label: tr ? '7g hata' : 'Failed 7d', value: fmt(engagement.failed7d) },
        ]} />
        <AdminCard accent="blue" icon="📊" title={tr ? 'Modül Sağlığı' : 'Module Health'} stats={[
          { label: tr ? 'En çok kullanılan' : 'Most used', value: moduleLabel(engagement.mostUsedModule?.module, tr) },
          { label: tr ? 'En az kullanılan' : 'Least used', value: moduleLabel(engagement.leastUsedModule?.module, tr) },
          { label: tr ? 'Gerçek verisiz modül' : 'Real no-data modules', value: fmt(actualNoDataModuleRows) },
          { label: tr ? 'Gerçek verili modül' : 'Real modules with data', value: fmt(actualModulesWithData) },
        ]} />
      </div>

      <div className="adminEngagementTables">
        <div className="adminEngagementTableCard">
          <div className="adminEngagementTableHead">
            <strong>{tr ? 'En çok görüntülenen modüller' : 'Most viewed modules'}</strong>
            <span>{tr ? 'İlk 8 kayıt' : 'Top 8 rows'}</span>
          </div>
          <div className="adminEngagementTableScroll">
            {topModules.length ? topModules.map((m) => (
              <div className="adminEngagementTableRow" key={`top-${m.module}`}>
                <span>{moduleLabel(m.module, tr)}</span>
                <em className="adminEngagementMetrics">
                  <span><TrendValue value={fmt(m.views)} compact trendKey={`engagement.moduleBreakdown.${m.module}.views`} /> {tr ? 'görüntüleme' : 'views'}</span>
                  <span><TrendValue value={fmt(m.dataAdds)} compact trendKey={`engagement.moduleBreakdown.${m.module}.dataAdds`} /> {tr ? 'gerçek veri kaydı' : 'real records'}</span>
                </em>
              </div>
            )) : <div className="adminControlEmpty">{tr ? 'Modül verisi yok' : 'No module data'}</div>}
          </div>
        </div>

        <div className="adminEngagementTableCard">
          <div className="adminEngagementTableHead">
            <strong>{tr ? 'Veri eklenmeyen modül fırsatları' : 'No-data module opportunities'}</strong>
            <span>{tr ? 'Kullanıcıya göre' : 'By users'}</span>
          </div>
          <div className="adminEngagementTableScroll">
            {weakModules.length ? weakModules.map((m) => (
              <div className="adminEngagementTableRow" key={`weak-${m.module}`}>
                <span>{moduleLabel(m.module, tr)}</span>
                <em className="adminEngagementMetrics">
                  <span><TrendValue value={fmt(m.usersWithoutData)} compact trendKey={`engagement.moduleBreakdown.${m.module}.usersWithoutData`} /> {tr ? 'kullanıcıda veri yok' : 'users without data'}</span>
                  <span><TrendValue value={fmt(m.dataUsers)} compact trendKey={`engagement.moduleBreakdown.${m.module}.dataUsers`} /> {tr ? 'gerçek veri ekleyen' : 'real users with data'}</span>
                  <span><TrendValue value={fmt(m.actualDataRecords ?? m.dataAdds)} compact trendKey={`engagement.moduleBreakdown.${m.module}.actualDataRecords`} /> {tr ? 'kayıt' : 'records'}</span>
                </em>
              </div>
            )) : <div className="adminControlEmpty">{tr ? 'Modül fırsatı yok' : 'No module opportunity'}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}


const DEFAULT_ADMIN_PREMIUM_CONFIG = {
  premiumEnabled: false,
  limits: {
    alisverisOge: 10,
    aktifGorev: 10,
    aylikTakvim: 10,
    aylikFinans: 15,
    saglikKayitKisi: 10,
    cocukProfil: 1,
    belgeSayisi: 5,
    belgeDepoMB: 30,
    mesajGecmis: 30,
    kayitliYer: 3,
    aileUye: 3,
    kullaniciAile: 1,
    premiumPlusToplamUye: 5,
    gunlukNePisirsam: 2,
  },
  lockedFeatures: {
    finansRapor: true,
    finansBirikim: true,
    finansTaksit: true,
    finansAbonelik: true,
    finansFatura: true,
    canliKonum: true,
    geofence: true,
    konumGecmisi: true,
    evcilHayvanProfil: true,
    haftalikMenu: true,
    cokluSohbetOdasi: true,
    zamanlanmisBildirim: true,
    otomatikBildirim: true,
    cokluAile: true,
  },
};

function AdminToggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      className={`adminControlToggle ${checked ? 'on' : ''}`}
      onClick={() => !disabled && onChange?.(!checked)}
      disabled={disabled}
      aria-pressed={!!checked}
    >
      <span />
    </button>
  );
}

function PanelHeader({ icon, title, subtitle, right }) {
  return (
    <div className="adminControlPanelHeader">
      <div className="adminControlPanelTitle">
        <div className="adminControlPanelIcon">{icon}</div>
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {right && <div className="adminControlPanelRight">{right}</div>}
    </div>
  );
}

function EmptyMiniState({ text }) {
  return <div className="adminControlEmpty">{text}</div>;
}

function NotificationCenterPanel({ tr, user }) {
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [titleTr, setTitleTr] = useState('');
  const [bodyTr, setBodyTr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [u, h] = await Promise.all([
        tumUygulamaKullanicilariniGetir(),
        bildirimGecmisiniGetir(null, 30),
      ]);
      setUsers(Array.isArray(u) ? u : []);
      setHistory(Array.isArray(h) ? h : []);
    } catch (e) {
      setStatus(tr ? 'Bildirim verileri alınamadı.' : 'Notification data could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Panel yalnız açıldığında güncellenir. Gönderimden hemen önce yapılan
    // zorunlu tazeleme korunur; kapalı/açık panelde periyodik trafik üretilmez.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!titleTr.trim() || !bodyTr.trim()) {
      setStatus(tr ? 'TR başlık ve mesaj zorunlu.' : 'TR title and message are required.');
      return;
    }
    try {
      setSending(true);
      setStatus('');
      const freshUsers = await tumUygulamaKullanicilariniGetir();
      const liveUsers = Array.isArray(freshUsers) ? freshUsers : users;
      setUsers(liveUsers);
      const result = await tumKullanicilaraSistemDuyurusuGonder({
        kullanicilar: liveUsers,
        baslik: titleTr.trim(),
        aciklama: bodyTr.trim(),
        baslikI18n: titleEn.trim() ? { tr: titleTr.trim(), en: titleEn.trim() } : null,
        aciklamaI18n: bodyEn.trim() ? { tr: bodyTr.trim(), en: bodyEn.trim() } : null,
        gonderenUid: user?.uid,
      });
      setStatus(result.pushSent > 0
        ? (tr
          ? `✓ ${result.pushSent} cihaza push gönderildi · ${result.inAppCreated} uygulama içi kayıt · ${result.pushFailed} hata`
          : `✓ Push sent to ${result.pushSent} devices · ${result.inAppCreated} in-app records · ${result.pushFailed} failed`)
        : (tr
          ? `Push hiçbir cihaza ulaşmadı. ${result.inAppCreated} uygulama içi kayıt oluşturuldu.`
          : `Push did not reach any device. ${result.inAppCreated} in-app records were created.`));
      setTitleTr(''); setBodyTr(''); setTitleEn(''); setBodyEn('');
      await load();
    } catch (e) {
      setStatus(tr ? 'Gönderim başarısız.' : 'Sending failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="adminControlPanel">
      <PanelHeader
        icon="🔔"
        title={tr ? 'Bildirim Merkezi' : 'Notification Center'}
        subtitle={tr ? 'Sistem duyurusu gönder, son bildirimleri kontrol et.' : 'Send system announcements and review recent notifications.'}
        right={<button className="adminGhostBtn compact" onClick={load} disabled={loading} type="button">{tr ? 'Yenile' : 'Refresh'}</button>}
      />
      <div className="adminControlStatsRow">
        <StatPill label={tr ? 'Hedef kullanıcı' : 'Target users'} value={fmt(users.length)} />
        <StatPill label={tr ? 'TR' : 'TR'} value={fmt(users.filter((u) => (u.locale || 'tr') !== 'en').length)} />
        <StatPill label={tr ? 'EN' : 'EN'} value={fmt(users.filter((u) => u.locale === 'en').length)} />
      </div>
      <div className="adminControlFormGrid">
        <label className="adminControlField"><span>TR {tr ? 'Başlık' : 'Title'}</span><input value={titleTr} onChange={(e) => setTitleTr(e.target.value)} /></label>
        <label className="adminControlField"><span>EN {tr ? 'Başlık' : 'Title'}</span><input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} /></label>
        <label className="adminControlField wide"><span>TR {tr ? 'Mesaj' : 'Message'}</span><textarea rows={3} value={bodyTr} onChange={(e) => setBodyTr(e.target.value)} /></label>
        <label className="adminControlField wide"><span>EN {tr ? 'Mesaj' : 'Message'}</span><textarea rows={3} value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} /></label>
      </div>
      <button className="adminPrimaryBtn" onClick={send} disabled={sending || loading} type="button">{sending ? (tr ? 'Gönderiliyor...' : 'Sending...') : (tr ? 'Tüm Kullanıcılara Gönder' : 'Send to All Users')}</button>
      {status && <div className={status.includes('✓') ? 'adminSuccessBox' : 'adminErrorBox'}>{status}</div>}
      <div className="adminControlSubTitle">{tr ? 'Son bildirimler' : 'Recent notifications'}</div>
      <div className="adminScrollableList compactList">
        {history.length ? history.slice(0, 20).map((item, idx) => (
          <div className="adminListItem" key={item.id || item.messageId || idx}>
            <strong>{item.title || item.baslik || item.module || '-'}</strong>
            <span>{item.body || item.aciklama || item.status || ''}</span>
            <small>{item.createdAt || item.created_at ? new Date(item.createdAt || item.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : ''}</small>
          </div>
        )) : <EmptyMiniState text={loading ? (tr ? 'Yükleniyor...' : 'Loading...') : (tr ? 'Kayıt yok' : 'No records')} />}
      </div>
    </div>
  );
}

function CronAutomationPanel({ tr }) {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('');
  const financePolicy = rules.find((rule) => rule.key === 'financeReminder') || null;

  const load = async () => {
    try {
      setLoading(true);
      const [policyData, l] = await Promise.all([
        bildirimPolitikalariGetir(),
        otomatikBildirimLoglariniGetir(30),
      ]);
      setRules(Array.isArray(policyData?.policies) ? policyData.policies : []);
      setLogs(Array.isArray(l) ? l : []);
    } catch (e) {
      setStatus(tr ? 'Otomasyon kuralları alınamadı.' : 'Automation rules could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRule = async (rule) => {
    try {
      const next = { ...rule, enabled: !rule.enabled };
      setSavingKey(rule.key);
      setRules((prev) => prev.map((r) => r.key === rule.key ? next : r));
      await bildirimPolitikasiKaydet(rule.key, next);
      setStatus(tr ? '✓ Kural güncellendi.' : '✓ Rule updated.');
    } catch (e) {
      setStatus(tr ? 'Kural kaydedilemedi.' : 'Rule could not be saved.');
      await load();
    } finally {
      setSavingKey('');
    }
  };

  const runNow = async () => {
    try {
      setRunning(true);
      setStatus('');
      const result = await otomatikBildirimleriSimdiCalistir();
      const sent = result?.result?.sent ?? result?.sent ?? result?.totalSent ?? 0;
      const failed = result?.result?.failed ?? result?.failed ?? result?.totalFailed ?? 0;
      setStatus(tr ? `✓ Kontrol tamamlandı. Gönderilen: ${sent}, hata: ${failed}` : `✓ Check completed. Sent: ${sent}, failed: ${failed}`);
      await load();
    } catch (e) {
      setStatus(tr ? 'Otomasyon çalıştırılamadı.' : 'Automation could not be run.');
    } finally {
      setRunning(false);
    }
  };

  const updateFinancePolicy = (patch) => setRules((current) => current.map((rule) => rule.key === 'financeReminder' ? { ...rule, ...patch } : rule));
  const updateFinanceRule = (ruleKey, patch) => updateFinancePolicy({
    rules: (financePolicy?.rules || []).map((rule) => rule.key === ruleKey ? { ...rule, ...patch } : rule),
  });
  const addFinanceRule = () => updateFinancePolicy({
    rules: [...(financePolicy?.rules || []), { key:`custom_${Date.now()}`, label:tr ? 'Yeni ödeme hatırlatması' : 'New payment reminder', enabled:true, financeKind:'bill', daysBefore:1, time:'09:00', custom:true }],
  });
  const saveFinancePolicy = async () => {
    if (!financePolicy) return;
    try {
      setSavingKey(financePolicy.key);
      await bildirimPolitikasiKaydet(financePolicy.key, financePolicy);
      setStatus(tr ? '✓ Finans otomasyonu kaydedildi.' : '✓ Finance automation saved.');
    } catch (_) {
      setStatus(tr ? 'Finans otomasyonu kaydedilemedi.' : 'Finance automation could not be saved.');
    } finally { setSavingKey(''); }
  };
  const resetPolicies = async () => {
    try {
      setSavingKey('defaults');
      await bildirimPolitikalariVarsayilanaKur();
      await load();
      setStatus(tr ? '✓ Varsayılan otomasyon ayarları geri yüklendi.' : '✓ Default automation settings restored.');
    } catch (_) {
      setStatus(tr ? 'Varsayılan ayarlar geri yüklenemedi.' : 'Default settings could not be restored.');
    } finally { setSavingKey(''); }
  };

  return (
    <div className="adminControlPanel">
      <PanelHeader
        icon="⚙️"
        title={tr ? 'Cron / Otomasyon' : 'Cron / Automation'}
        subtitle={tr ? 'Aktif bildirim politikalarını alıcıların yerel saatine göre kontrol et.' : 'Control active notification policies in each recipient’s local time.'}
        right={<button className="adminPrimaryBtn compact" onClick={runNow} disabled={running || loading} type="button">{running ? (tr ? 'Çalışıyor...' : 'Running...') : (tr ? 'Şimdi Kontrol Et' : 'Run Now')}</button>}
      />
      <div className="adminSuccessBox">
        {tr
          ? 'Saatler her kullanıcının yerel saatine göre uygulanır. Saat dilimi bilinmiyorsa Europe/Istanbul kullanılır.'
          : 'Times use each user’s local time. Europe/Istanbul is used when the time zone is unavailable.'}
      </div>
      <div className="adminScrollableList ruleList">
        {rules.length ? rules.map((rule) => (
          <div className={`adminRuleItem ${rule.enabled ? 'active' : ''}`} key={rule.key}>
            <div>
              <strong>{rule.icon} {rule.title || rule.key}</strong>
              <span>{rule.description || ''}</span>
              <span>
                {(rule.rules || [])
                  .filter((item) => item?.enabled !== false)
                  .map((item) => item.usesOwnTime
                    ? (tr ? `${item.label || item.key}: kayıt saati` : `${item.label || item.key}: record time`)
                    : `${item.label || item.key}: ${item.time || '09:00'}`)
                  .join(' · ')}
              </span>
            </div>
            <AdminToggle checked={!!rule.enabled} onChange={() => toggleRule(rule)} disabled={savingKey === rule.key} />
          </div>
        )) : <EmptyMiniState text={loading ? (tr ? 'Yükleniyor...' : 'Loading...') : (tr ? 'Kural yok' : 'No rules')} />}
      </div>
      {financePolicy && (
        <div className="adminControlPanel financeAutomationEditor">
          <div className="adminControlSubTitle">{tr ? 'Finans bildirim ayrıntıları' : 'Finance notification details'}</div>
          <div className="adminControlFormGrid two">
            <label className="adminControlField"><span>{tr ? 'Günlük limit' : 'Daily limit'}</span><input type="number" min="1" max="99" value={financePolicy.dailyLimit || 8} onChange={(e) => updateFinancePolicy({ dailyLimit:Number(e.target.value || 1) })} /></label>
            <label className="adminControlField"><span>{tr ? 'Dağıtma süresi (dk)' : 'Spread window (min)'}</span><input type="number" min="0" max="360" value={financePolicy.deliverySpreadMinutes ?? 120} onChange={(e) => updateFinancePolicy({ deliverySpreadMinutes:Number(e.target.value || 0) })} /></label>
            <div className="adminControlField"><span>{tr ? 'Sessiz saatlere uy' : 'Respect quiet hours'}</span><AdminToggle checked={financePolicy.respectGlobalQuietHours !== false} onChange={(value) => updateFinancePolicy({ respectGlobalQuietHours:value })} /></div>
            <div className="adminControlField"><span>{tr ? 'Tekrar bildirimi engelle' : 'Prevent duplicates'}</span><AdminToggle checked={financePolicy.preventDuplicates !== false} onChange={(value) => updateFinancePolicy({ preventDuplicates:value })} /></div>
          </div>
          <div className="adminScrollableList ruleList">
            {(financePolicy.rules || []).map((rule) => {
              const savingRule = String(rule.type || '').startsWith('saving');
              return (
                <div className="adminRuleItem active" key={rule.key}>
                  <div className="adminControlFormGrid two">
                    <label className="adminControlField"><span>{tr ? 'Kural adı' : 'Rule name'}</span><input value={rule.label || ''} onChange={(e) => updateFinanceRule(rule.key, { label:e.target.value })} /></label>
                    {!savingRule && <label className="adminControlField"><span>{tr ? 'Kayıt türü' : 'Record type'}</span><select value={rule.financeKind || 'bill'} onChange={(e) => updateFinanceRule(rule.key, { financeKind:e.target.value })}><option value="installment">{tr ? 'Taksit' : 'Installment'}</option><option value="subscription">{tr ? 'Abonelik' : 'Subscription'}</option><option value="bill">{tr ? 'Fatura' : 'Bill'}</option></select></label>}
                    {!savingRule && <label className="adminControlField"><span>{tr ? 'Kaç gün önce' : 'Days before'}</span><input type="number" min="0" max="365" value={rule.daysBefore ?? 0} onChange={(e) => updateFinanceRule(rule.key, { daysBefore:Number(e.target.value || 0) })} /></label>}
                    {savingRule && <label className="adminControlField"><span>{tr ? 'Hareketsizlik günü' : 'Inactivity days'}</span><input type="number" min="1" max="730" value={rule.thresholdDays || 30} onChange={(e) => updateFinanceRule(rule.key, { thresholdDays:Number(e.target.value || 1) })} /></label>}
                    {savingRule && <label className="adminControlField"><span>{tr ? 'Tekrar aralığı (gün)' : 'Repeat every (days)'}</span><input type="number" min="1" max="365" value={rule.repeatEveryDays || 14} onChange={(e) => updateFinanceRule(rule.key, { repeatEveryDays:Number(e.target.value || 1) })} /></label>}
                    {savingRule && <label className="adminControlField"><span>{tr ? 'Maksimum tekrar' : 'Maximum repeats'}</span><input type="number" min="1" max="20" value={rule.maxRepeat || 3} onChange={(e) => updateFinanceRule(rule.key, { maxRepeat:Number(e.target.value || 1) })} /></label>}
                    <label className="adminControlField"><span>{tr ? 'Saat' : 'Time'}</span><input type="time" value={rule.time || '09:00'} onChange={(e) => updateFinanceRule(rule.key, { time:e.target.value })} /></label>
                  </div>
                  <AdminToggle checked={rule.enabled !== false} onChange={(value) => updateFinanceRule(rule.key, { enabled:value })} />
                  {rule.custom && <button className="adminDangerBtn" type="button" onClick={() => updateFinancePolicy({ rules:(financePolicy.rules || []).filter((item) => item.key !== rule.key) })}>{tr ? 'Sil' : 'Delete'}</button>}
                </div>
              );
            })}
          </div>
          <div className="adminPrimaryActions compactActions">
            <button className="adminGhostBtn" type="button" onClick={addFinanceRule}>{tr ? 'Hatırlatma günü ekle' : 'Add reminder day'}</button>
            <button className="adminGhostBtn" type="button" disabled={savingKey === 'defaults'} onClick={resetPolicies}>{tr ? 'Varsayılana dön' : 'Restore defaults'}</button>
            <button className="adminPrimaryBtn" type="button" disabled={savingKey === financePolicy.key} onClick={saveFinancePolicy}>{tr ? 'Finans Ayarlarını Kaydet' : 'Save Finance Settings'}</button>
          </div>
        </div>
      )}
      {status && <div className={status.includes('✓') ? 'adminSuccessBox' : 'adminErrorBox'}>{status}</div>}
      <div className="adminControlSubTitle">{tr ? 'Son cron kayıtları' : 'Recent cron logs'}</div>
      <div className="adminScrollableList compactList small">
        {logs.length ? logs.slice(0, 12).map((log, idx) => (
          <div className="adminListItem" key={log.id || idx}>
            <strong>{log.status || log.type || '-'}</strong>
            <span>{tr ? `Gönderilen: ${log.totalSent ?? log.sent ?? 0} · Hata: ${log.totalFailed ?? log.failed ?? 0}` : `Sent: ${log.totalSent ?? log.sent ?? 0} · Failed: ${log.totalFailed ?? log.failed ?? 0}`}</span>
            <small>{log.createdAt || log.created_at ? new Date(log.createdAt || log.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : ''}</small>
          </div>
        )) : <EmptyMiniState text={tr ? 'Log yok' : 'No logs'} />}
      </div>
    </div>
  );
}

function MailResendPanel({ tr, t, lang }) {
  return (
    <div className="adminControlPanel">
      <PanelHeader icon="✉️" title={tr ? 'E-posta Sistemi' : 'Email System'} subtitle={tr ? 'Brevo, Resend ve gönderim kullanımını izleyin.' : 'Monitor Brevo, Resend, and delivery usage.'} />
      <EmailSystemAdmin t={t} lang={lang} />
    </div>
  );
}

function PremiumSettingsPanel({ tr }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const cfg = await getPremiumConfig();
      setConfig({
        ...DEFAULT_ADMIN_PREMIUM_CONFIG,
        ...(cfg || {}),
        limits: { ...DEFAULT_ADMIN_PREMIUM_CONFIG.limits, ...(cfg?.limits || {}) },
        lockedFeatures: { ...DEFAULT_ADMIN_PREMIUM_CONFIG.lockedFeatures, ...(cfg?.lockedFeatures || {}) },
      });
    } catch (e) {
      setStatus(tr ? 'Premium ayarları alınamadı.' : 'Premium settings could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLimit = (key, value) => setConfig((prev) => ({ ...prev, limits: { ...(prev?.limits || {}), [key]: Number(value || 0) } }));
  const setFeature = (key, value) => setConfig((prev) => ({ ...prev, lockedFeatures: { ...(prev?.lockedFeatures || {}), [key]: value } }));

  const save = async () => {
    try {
      setSaving(true);
      setStatus('');
      const saved = await savePremiumConfig(config);
      setConfig(saved);
      setStatus(tr ? '✓ Premium ayarları kaydedildi.' : '✓ Premium settings saved.');
    } catch (e) {
      setStatus(tr ? 'Premium ayarları kaydedilemedi.' : 'Premium settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="adminControlPanel"><EmptyMiniState text={tr ? 'Premium ayarları yükleniyor...' : 'Loading premium settings...'} /></div>;
  if (!config) return <div className="adminControlPanel"><EmptyMiniState text={tr ? 'Ayar bulunamadı' : 'No settings found'} /></div>;

  return (
    <div className="adminControlPanel">
      <PanelHeader
        icon="💎"
        title={tr ? 'Premium Ayarları' : 'Premium Settings'}
        subtitle={tr ? 'Limitleri ve kilitli özellikleri dashboard içinde düzenle.' : 'Edit limits and locked features inside the dashboard.'}
        right={<AdminToggle checked={!!config.premiumEnabled} onChange={(v) => setConfig((prev) => ({ ...prev, premiumEnabled: v }))} />}
      />
      <div className="adminControlSubTitle">{tr ? 'Limitler' : 'Limits'}</div>
      <div className="adminScrollableGrid limitGrid">
        {Object.entries(config.limits || {}).map(([key, value]) => (
          <label className="adminControlField mini" key={key}>
            <span>{key}</span>
            <input type="number" min="0" value={value ?? 0} onChange={(e) => setLimit(key, e.target.value)} />
          </label>
        ))}
      </div>
      <div className="adminControlSubTitle">{tr ? 'Kilitli özellikler' : 'Locked features'}</div>
      <div className="adminScrollableList featureList">
        {Object.entries(config.lockedFeatures || {}).map(([key, value]) => (
          <div className="adminRuleItem" key={key}>
            <div><strong>{key}</strong><span>{value ? (tr ? 'Premium gerektirir' : 'Requires premium') : (tr ? 'Açık' : 'Open')}</span></div>
            <AdminToggle checked={!!value} onChange={(v) => setFeature(key, v)} />
          </div>
        ))}
      </div>
      <button className="adminPrimaryBtn" type="button" onClick={save} disabled={saving}>{saving ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Ayarları Kaydet' : 'Save Settings')}</button>
      {status && <div className={status.includes('✓') ? 'adminSuccessBox' : 'adminErrorBox'}>{status}</div>}
    </div>
  );
}

function AiAssistantPanel({ tr }) {
  return (
    <div className="adminControlPanel aiEmbeddedPanel">
      <PanelHeader icon="🤖" title={tr ? 'AI Asistan Bildirimleri' : 'AI Assistant Notifications'} subtitle={tr ? 'Mevcut AI bildirim ayarları dashboard içine taşındı.' : 'Existing AI notification settings are embedded into the dashboard.'} />
      <div className="adminEmbeddedScroll">
        <AssistantNotificationAdmin />
      </div>
    </div>
  );
}

function ExportPanel({ tr, exportJobs, exportData, exporting }) {
  return (
    <div className="adminControlPanel">
      <PanelHeader icon="📦" title={tr ? 'Veri Dışa Aktarma' : 'Data Export'} subtitle={tr ? 'Güvenli veya tam çıktıyı oluştur ve son exportları takip et.' : 'Create safe or full exports and track recent jobs.'} />
      <div className="adminPrimaryActions compactActions">
        <button className="adminGhostBtn" onClick={() => exportData('safe')} disabled={exporting} type="button">{tr ? 'Güvenli Çıktı' : 'Safe Export'}</button>
        <button className="adminDangerBtn" onClick={() => exportData('full')} disabled={exporting} type="button">{tr ? 'Tam Çıktı' : 'Full Export'}</button>
      </div>
      <div className="adminScrollableList compactList">
        {(exportJobs || []).length ? exportJobs.slice(0, 8).map((job, idx) => (
          <div className="adminListItem" key={job.id || idx}>
            <strong>{job.file_name || job.type || 'export'}</strong>
            <span>{job.status || '-'}</span>
            <small>{job.created_at ? new Date(job.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : ''}</small>
          </div>
        )) : <EmptyMiniState text={tr ? 'Henüz export yok' : 'No export yet'} />}
      </div>
    </div>
  );
}

export default function AdminDashboardPage({ user, profil, setAktifSayfa, onExitAdmin }) {
  const { lang, t } = useLanguage();
  const [snapshot, setSnapshot] = useState(null);
  const [exportJobs, setExportJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeControl, setActiveControl] = useState('notifications');
  const [adminLocked, setAdminLocked] = useState(true);
  const [adminPassword, setAdminPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [trendRefreshSignal, setTrendRefreshSignal] = useState(0);
  const [refreshTrends, setRefreshTrends] = useState({});
  const latestSummaryRef = useRef({});
  const controlHostRef = useRef(null);

  const tr = lang !== 'en';
  const locale = lang === 'en' ? 'en-US' : 'tr-TR';
  const txt = {
    title: tr ? 'Admin Dashboard' : 'Admin Dashboard',
    sub: tr ? 'Süperadmin operasyon merkezi' : 'Super admin operations center',
    exit: tr ? 'Aileye Dön' : 'Back',
    refresh: tr ? 'Verileri Güncelle' : 'Refresh Data',
    detail: tr ? 'Detaylı Rapor' : 'Detailed Report',
    detailClose: tr ? 'Raporu Kapat' : 'Close Report',
    safeExport: tr ? 'Güvenli Çıktı' : 'Safe Export',
    fullExport: tr ? 'Tam Çıktı' : 'Full Export',
    last: tr ? 'Son güncelleme' : 'Last update',
    noSnapshot: tr ? 'Henüz snapshot yok. İstatistikleri manuel güncelle.' : 'No snapshot yet. Refresh statistics manually.',
    manage: tr ? 'Panele Git' : 'Open Panel',
    loading: tr ? 'Yükleniyor...' : 'Loading...',
    updating: tr ? 'Güncelleniyor...' : 'Refreshing...',
    lockTitle: tr ? 'Admin güvenlik kilidi' : 'Admin security lock',
    lockSub: tr ? 'Devam etmek için ek admin şifresini gir.' : 'Enter the extra admin password to continue.',
    password: tr ? 'Admin şifresi' : 'Admin password',
    unlock: tr ? 'Kilidi Aç' : 'Unlock',
    locking: tr ? 'Kontrol ediliyor...' : 'Checking...',
    lockNow: tr ? 'Kilitle' : 'Lock',
  };
  const controlLabels = {
    notifications: tr ? 'Bildirim Merkezi' : 'Notification Center',
    cron: tr ? 'Cron / Otomasyon' : 'Cron / Automation',
    mail: tr ? 'E-posta Sistemi' : 'Email System',
    ai: tr ? 'AI Asistan Bildirimleri' : 'AI Assistant Notifications',
    engagement: tr ? 'Kullanıcı Hatırlatma Merkezi' : 'User Reminder Center',
    liveActivity: tr ? 'Canlı Kullanıcı Aktivitesi' : 'Live User Activity',
    siteTraffic: tr ? 'Web Sitesi Trafiği' : 'Website Traffic',
    operations: tr ? 'Operasyon ve Sistem Merkezi' : 'Operations & System Center',
    feedback: t('admin.feedback.title'),
    deletionSurvey: tr ? 'Hesap Silme Anketi' : 'Account Deletion Survey',
    premium: tr ? 'Premium Ayarları' : 'Premium Settings',
    export: tr ? 'Veri Dışa Aktarma' : 'Data Export',
  };

  const openControl = (control) => {
    setActiveControl(control);
    window.requestAnimationFrame(() => {
      controlHostRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const summary = sanitizeSummary(snapshot?.summary || {});
  const generatedAt = snapshot?.created_at || summary?.generatedAt;

  const load = async () => {
    try {
      setError('');
      const status = await getAdminSessionStatus();
      setLockInfo(status);
      if (!status.unlocked) {
        setAdminLocked(true);
        setSnapshot(null);
        latestSummaryRef.current = {};
        setRefreshTrends({});
        setExportJobs([]);
        return;
      }
      setAdminLocked(false);
      const data = await getAdminDashboardSummary();
      const loadedSnapshot = data.snapshot || null;
      setSnapshot(loadedSnapshot);
      latestSummaryRef.current = sanitizeSummary(loadedSnapshot?.summary || {});
      setExportJobs(data.exportJobs || []);
    } catch (e) {
      if (e.code === 'admin_locked' || e.status === 423) {
        clearAdminSessionToken();
        setAdminLocked(true);
      } else {
        setError(e.message || (tr ? 'Admin verileri alınamadı.' : 'Admin data could not be loaded.'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (event) => {
    event?.preventDefault?.();
    if (!adminPassword.trim()) return;
    try {
      setUnlocking(true);
      setError('');
      const data = await unlockAdminDashboard(adminPassword);
      setAdminPassword('');
      setLockInfo(data);
      setAdminLocked(false);
      await load();
    } catch (e) {
      setLockInfo(e.payload || null);
      setError(tr ? 'Hatalı giriş.' : 'Invalid login.');
    } finally {
      setUnlocking(false);
    }
  };

  const lockNow = async () => {
    try { await lockAdminDashboard(); } catch {}
    setAdminLocked(true);
    setSnapshot(null);
    latestSummaryRef.current = {};
    setRefreshTrends({});
    setExportJobs([]);
  };

  const refresh = async () => {
    try {
      setRefreshing(true);
      setError('');
      const data = await refreshAdminStats();
      const nextSnapshot = data.snapshot || null;
      const nextSummary = sanitizeSummary(nextSnapshot?.summary || {});
      const previousSummary = sanitizeSummary(latestSummaryRef.current || snapshot?.summary || {});
      const trendMap = buildTrendMap(previousSummary, nextSummary);
      setRefreshTrends(trendMap);
      setSnapshot(nextSnapshot);
      latestSummaryRef.current = nextSummary;
      setTrendRefreshSignal((v) => v + 1);
    } catch (e) {
      if (e.code === 'admin_locked' || e.status === 423) {
        clearAdminSessionToken();
        setAdminLocked(true);
      }
      setError(e.message || (tr ? 'İstatistikler güncellenemedi.' : 'Statistics could not be refreshed.'));
    } finally {
      setRefreshing(false);
    }
  };

  const exportData = async (type) => {
    try {
      setExporting(true);
      setError('');
      const data = await createAdminExport(type);
      const job = data.job;
      setExportJobs((prev) => [job, ...prev].filter(Boolean).slice(0, 5));
      if (job?.id) {
        const blob = await downloadAdminExport(job.id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = job.file_name || job.fileName || 'admin-export.zip';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      if (e.code === 'admin_locked' || e.status === 423) {
        clearAdminSessionToken();
        setAdminLocked(true);
      }
      setError(e.message || (tr ? 'Çıktı oluşturulamadı.' : 'Export could not be created.'));
    } finally {
      setExporting(false);
    }
  };

  const healthScore = useMemo(() => {
    const sent = n(summary.notifications?.success);
    const fail = n(summary.notifications?.failed);
    const total = sent + fail;
    if (!total) return 100;
    return Math.max(0, Math.round((sent / total) * 100));
  }, [summary]);

  const topStats = [
    { label: tr ? 'Kullanıcı' : 'Users', value: fmt(summary.users?.total), hint: `${fmt(summary.users?.tr)} TR · ${fmt(summary.users?.en)} EN` },
    { label: tr ? 'Aile' : 'Families', value: fmt(summary.families?.total), hint: tr ? `${fmt(summary.families?.memberships)} aktif üye kaydı` : `${fmt(summary.families?.memberships)} active member rows` },
    { label: tr ? 'Bildirim' : 'Notify', value: `${healthScore}%`, hint: tr ? 'başarı' : 'success' },
  ];

  const warnings = [];
  if (healthScore < 90) warnings.push(tr ? 'Bildirim başarı oranı düşük' : 'Notification success is low');
  if (n(summary.push?.total) === 0) warnings.push(tr ? 'Kayıtlı push cihazı yok' : 'No push devices registered');
  if (!generatedAt) warnings.push(tr ? 'Henüz istatistik snapshot alınmadı' : 'No statistics snapshot yet');

  if (adminLocked) {
    const lockedUntil = lockInfo?.lockedUntil ? new Date(lockInfo.lockedUntil).toLocaleString(locale) : null;
    return (
      <div className="adminDashboardPage modernHome adminLockPage">
        <div className="adminHomeTopBar">
          <div className="adminTitleWrap">
            <div className="adminWelcomeLabel">SUPER ADMIN</div>
            <h1>{txt.lockTitle}</h1>
            <p>{profil?.email || user?.email}</p>
          </div>
          <button className="adminExitBtn" onClick={onExitAdmin} type="button">{txt.exit}</button>
        </div>
        <form className="adminLockCard" onSubmit={unlock}>
          <div className="adminLockIcon">🔐</div>
          <h2>{txt.lockTitle}</h2>
          <p>{loading ? (tr ? 'Güvenli oturum kontrol ediliyor...' : 'Checking secure session...') : (lockedUntil ? (tr ? `Çok fazla yanlış deneme. Tekrar deneme zamanı: ${lockedUntil}` : `Too many wrong attempts. Try again at: ${lockedUntil}`) : txt.lockSub)}</p>
          <label className="adminLockInputWrap">
            <span>{txt.password}</span>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading || unlocking || !!lockedUntil}
              placeholder="••••••••"
            />
          </label>
          {error && <div className="adminErrorBox">⚠️ {error}</div>}
          <button className="adminPrimaryBtn adminLockButton" type="submit" disabled={loading || unlocking || !adminPassword.trim() || !!lockedUntil}>
            {loading || unlocking ? txt.locking : txt.unlock}
          </button>
        </form>
      </div>
    );
  }

  return (
    <AdminTrendRefreshContext.Provider value={{ refreshSignal: trendRefreshSignal, trends: refreshTrends }}>
      <div className="adminDashboardPage modernHome">
      <div className="adminHomeTopBar">
        <div className="adminTitleWrap">
          <div className="adminWelcomeLabel">{txt.sub}</div>
          <h1>{txt.title}</h1>
          <p>{profil?.email || user?.email}</p>
        </div>
        <div className="adminTopActions">
          <button className="adminLockSmallBtn" onClick={lockNow} type="button">{txt.lockNow}</button>
          <button className="adminExitBtn" onClick={onExitAdmin} type="button">{txt.exit}</button>
        </div>
      </div>

      <section className="adminHeroCard">
        <span className="heroOrb adminHeroOrb1" />
        <span className="heroOrb adminHeroOrb2" />
        <span className="heroShine" />
        <div className="adminHeroContent">
          <div className="heroCardBadge">SUPER ADMIN</div>
          <h2>{tr ? 'Operasyon Merkezi' : 'Operations Center'}</h2>
          <p>
            {tr
              ? 'Sistem sağlığı, kullanıcılar, bildirimler, exportlar ve detaylı istatistikler tek güvenli panelde.'
              : 'System health, users, notifications, exports and detailed statistics in one secure panel.'}
          </p>
          <div className="adminHeroMeta">
            <strong>{healthScore}/100</strong>
            <span>{tr ? 'Sistem skoru' : 'System score'}</span>
          </div>
        </div>
        <div className="adminHeroIcon">🛡️</div>
      </section>

      <div className="adminHealthStrip">
        <HealthChip label="API" ok />
        <HealthChip label="DB" ok />
        <HealthChip label="Redis" ok />
        <HealthChip label="Push" ok={n(summary.push?.total) > 0} />
        <HealthChip label="Cron" ok />
      </div>

      <div className="adminMiniStats">
        {topStats.map((item) => <StatPill key={item.label} label={item.label} value={item.value} hint={item.hint} />)}
      </div>

      {error && <div className="adminErrorBox">⚠️ {error}</div>}

      <section className={`adminWarningPanel ${warnings.length ? 'hasWarnings' : ''}`}>
        <div>
          <span>{tr ? 'Kritik durum' : 'Critical status'}</span>
          <strong>{warnings.length ? `${warnings.length} ${tr ? 'uyarı' : 'warning'}` : (tr ? 'Sorun yok' : 'No issues')}</strong>
        </div>
        <p>{warnings.length ? warnings.slice(0, 2).join(' · ') : (tr ? 'Sistem genel görünümü sağlıklı görünüyor.' : 'System overview looks healthy.')}</p>
      </section>

      <div className="adminSectionHeader adminModulesHeader">
        <div>
          <span>{tr ? 'Yönetim modülleri' : 'Management modules'}</span>
          <h2>{tr ? 'Kontrol Kartları' : 'Control Cards'}</h2>
          <p>{tr ? 'Bir modül seçin; işlemler aşağıdaki sabit çalışma alanında açılır.' : 'Choose a module; its tools open in the fixed workspace below.'}</p>
        </div>
      </div>

      <div className="adminControlOverview" aria-label={tr ? 'Sistem sağlık özeti' : 'System health summary'}>
        <div className="adminControlOverviewLead">
          <span className="adminControlOverviewIcon" aria-hidden="true">🛡️</span>
          <div>
            <small>{tr ? 'Canlı sistem durumu' : 'Live system status'}</small>
            <strong>{healthScore}/100</strong>
          </div>
        </div>
        <div className="adminControlOverviewMetrics">
          <span><small>{tr ? 'Bildirim başarı' : 'Notification success'}</small><strong>{healthScore}%</strong></span>
          <span><small>{tr ? 'Push cihaz' : 'Push devices'}</small><strong>{fmt(summary.push?.total)}</strong></span>
        </div>
      </div>

      <div className="adminControlGroups">
        <section className="adminControlGroup" aria-labelledby="admin-communication-controls">
          <div className="adminControlGroupHeader"><span aria-hidden="true">✦</span><div><strong id="admin-communication-controls">{tr ? 'İletişim ve otomasyon' : 'Communication & automation'}</strong><small>{tr ? 'Bildirim, e-posta ve zamanlanmış akışlar' : 'Notifications, email and scheduled flows'}</small></div></div>
          <div className="adminCardsGrid adminControlCardsGrid">
            <AdminCard active={activeControl === 'notifications'} accent="rose" icon="🔔" title={tr ? 'Bildirim Merkezi' : 'Notification Center'} subtitle={tr ? 'Duyuru gönder, son kayıtları izle' : 'Send announcements and review logs'} actionLabel={activeControl === 'notifications' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('notifications')} />
            <AdminCard active={activeControl === 'cron'} accent="amber" icon="⚙️" title="Cron / Otomasyon" subtitle={tr ? 'Kurallar ve manuel tetikleme' : 'Rules and manual trigger'} actionLabel={activeControl === 'cron' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('cron')} />
            <AdminCard active={activeControl === 'mail'} accent="green" icon="✉️" title={tr ? 'E-posta Sistemi' : 'Email System'} subtitle={tr ? 'Brevo, Resend, kota ve gönderim sağlığı' : 'Brevo, Resend, quota, and delivery health'} actionLabel={activeControl === 'mail' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('mail')} />
            <AdminCard active={activeControl === 'ai'} accent="indigo" icon="🤖" title={tr ? 'AI Asistan Bildirimleri' : 'AI Assistant Notifications'} subtitle={tr ? 'AI bildirim ayarları ve loglar' : 'AI notification settings and logs'} actionLabel={activeControl === 'ai' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('ai')} />
            <AdminCard active={activeControl === 'engagement'} accent="rose" icon="🎯" title={tr ? 'Kullanıcı Hatırlatma Merkezi' : 'User Reminder Center'} subtitle={tr ? 'Onboarding, pasif kullanıcı ve modül tanıtım bildirimleri' : 'Onboarding, inactive user and module discovery reminders'} actionLabel={activeControl === 'engagement' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('engagement')} />
          </div>
        </section>
        <section className="adminControlGroup" aria-labelledby="admin-system-controls">
          <div className="adminControlGroupHeader"><span aria-hidden="true">⌁</span><div><strong id="admin-system-controls">{tr ? 'Kullanıcı ve sistem yönetimi' : 'User & system management'}</strong><small>{tr ? 'Canlı aktivite, operasyon, geri bildirim ve yapılandırma' : 'Live activity, operations, feedback and configuration'}</small></div></div>
          <div className="adminCardsGrid adminControlCardsGrid">
            <AdminCard active={activeControl === 'liveActivity'} accent="green" icon="◉" title={tr ? 'Canlı Kullanıcı Aktivitesi' : 'Live User Activity'} subtitle={tr ? 'Aktif kullanıcılar, süreler ve sürüm dağılımı' : 'Active users, windows and version distribution'} actionLabel={activeControl === 'liveActivity' ? (tr ? 'Açık' : 'Open') : (tr ? 'İncele' : 'Review')} onClick={() => openControl('liveActivity')} />
            <AdminCard active={activeControl === 'siteTraffic'} accent="indigo" icon="↗" title={tr ? 'Web Sitesi Trafiği' : 'Website Traffic'} subtitle={tr ? 'Ziyaretler, kampanyalar ve mağaza tıklamaları' : 'Visits, campaigns and store clicks'} actionLabel={activeControl === 'siteTraffic' ? (tr ? 'Açık' : 'Open') : (tr ? 'İncele' : 'Review')} onClick={() => openControl('siteTraffic')} />
            <AdminCard active={activeControl === 'operations'} accent="amber" icon="⌁" title={tr ? 'Operasyon ve Sistem Merkezi' : 'Operations & System Center'} subtitle={tr ? 'Sistem sağlığı, teslimat, güvenlik ve depolama' : 'System health, delivery, security and storage'} actionLabel={activeControl === 'operations' ? (tr ? 'Açık' : 'Open') : (tr ? 'İncele' : 'Review')} onClick={() => openControl('operations')} />
            <AdminCard active={activeControl === 'feedback'} accent="blue" icon="💬" title={t('admin.feedback.title')} subtitle={t('admin.feedback.cardSubtitle')} actionLabel={activeControl === 'feedback' ? t('admin.feedback.open') : t('admin.feedback.control')} onClick={() => openControl('feedback')} />
            <AdminCard active={activeControl === 'deletionSurvey'} accent="rose" icon="◫" title={tr ? 'Hesap Silme Anketi' : 'Account Deletion Survey'} subtitle={tr ? 'Hesap silme nedenleri ve kullanıcı ayrıntıları' : 'Deletion reasons and user details'} actionLabel={activeControl === 'deletionSurvey' ? (tr ? 'Açık' : 'Open') : (tr ? 'İncele' : 'Review')} onClick={() => openControl('deletionSurvey')} />
            <AdminCard active={activeControl === 'premium'} accent="blue" icon="💎" title={tr ? 'Premium Ayarları' : 'Premium Settings'} subtitle={tr ? 'Limitler ve kilitli özellikler' : 'Limits and locked features'} actionLabel={activeControl === 'premium' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('premium')} />
            <AdminCard active={activeControl === 'export'} accent="indigo" icon="📦" title={tr ? 'Veri Dışa Aktarma' : 'Data Export'} subtitle={(exportJobs || []).length ? `${exportJobs[0]?.status || ''} · ${exportJobs[0]?.file_name || ''}` : (tr ? 'Güvenli / tam çıktı' : 'Safe / full export')} actionLabel={activeControl === 'export' ? (tr ? 'Açık' : 'Open') : (tr ? 'Kontrol Et' : 'Control')} onClick={() => openControl('export')} />
          </div>
        </section>
      </div>

      <section className="adminControlHost" ref={controlHostRef} aria-live="polite">
        <div className="adminControlHostBar">
          <div className="adminControlHostIdentity"><span aria-hidden="true">↓</span><div><small>{tr ? 'Seçilen çalışma alanı' : 'Selected workspace'}</small><strong>{controlLabels[activeControl]}</strong></div></div>
          <span>{tr ? 'Uzun listeler kendi içinde kaydırılır' : 'Long lists scroll independently'}</span>
        </div>
        <div className="adminControlViewport" key={activeControl}>
          {activeControl === 'notifications' && <NotificationCenterPanel tr={tr} user={user} />}
          {activeControl === 'cron' && <CronAutomationPanel tr={tr} />}
          {activeControl === 'mail' && <MailResendPanel tr={tr} t={t} lang={lang} />}
          {activeControl === 'ai' && <AiAssistantPanel tr={tr} />}
          {activeControl === 'engagement' && <EngagementAdminPanel tr={tr} />}
          {activeControl === 'liveActivity' && <LiveUserActivityAdmin tr={tr} />}
          {activeControl === 'siteTraffic' && <CorporateTrafficAdmin tr={tr} />}
          {activeControl === 'operations' && <><PerformanceMonitoringAdmin tr={tr} requireAdminUnlock /><AdminOperationsOverview tr={tr} /></>}
          {activeControl === 'feedback' && <FeedbackAdminPanel tr={tr} />}
          {activeControl === 'deletionSurvey' && <AccountDeletionSurveyAdminPanel tr={tr} />}
          {activeControl === 'premium' && <PremiumSettingsPanel tr={tr} />}
          {activeControl === 'export' && <ExportPanel tr={tr} exportJobs={exportJobs} exportData={exportData} exporting={exporting} />}
        </div>
      </section>

      <section className="adminStatsPanel">
        <div className="adminSectionHeader">
          <div>
            <span>{tr ? 'Canlı sorgu yerine snapshot' : 'Snapshot instead of live heavy queries'}</span>
            <h2>{tr ? 'Detaylı İstatistikler' : 'Detailed Statistics'}</h2>
            <p>{generatedAt ? `${txt.last}: ${new Date(generatedAt).toLocaleString(locale)}` : txt.noSnapshot}</p>
          </div>
        </div>

        <div className="adminQuickGrid">
          <StatPill label={tr ? 'Toplam kullanıcı' : 'Total users'} value={fmt(summary.users?.total)} trendKey="users.total" />
          <StatPill label={tr ? 'TR kullanıcı' : 'TR users'} value={fmt(summary.users?.tr)} trendKey="users.tr" />
          <StatPill label={tr ? 'EN kullanıcı' : 'EN users'} value={fmt(summary.users?.en)} trendKey="users.en" />
          <StatPill label={tr ? 'Toplam aile' : 'Total families'} value={fmt(summary.families?.total)} trendKey="families.total" />
          <StatPill label={tr ? 'Chat mesajı' : 'Chat messages'} value={fmt(summary.chat?.messages)} trendKey="chat.messages" />
          <StatPill label={tr ? 'Belge boyutu' : 'Document size'} value={bytes(summary.documents?.bytes)} trendKey="documents.bytes" />
        </div>

        <div className="adminPrimaryActions">
          <button className="adminPrimaryBtn" onClick={refresh} disabled={refreshing || loading} type="button">
            {refreshing ? txt.updating : txt.refresh}
          </button>
          <button className="adminGhostBtn" onClick={() => setDetailOpen((v) => !v)} disabled={!snapshot} type="button">
            {detailOpen ? txt.detailClose : txt.detail}
          </button>
          <button className="adminGhostBtn" onClick={() => exportData('safe')} disabled={exporting} type="button">
            {txt.safeExport}
          </button>
          <button className="adminDangerBtn" onClick={() => exportData('full')} disabled={exporting} type="button">
            {txt.fullExport}
          </button>
        </div>

        {detailOpen && (
          <>
          <div className="adminDetailGrid">
            <AdminCard accent="blue" icon="👥" title={tr ? 'Kullanıcılar' : 'Users'} stats={[
              { label: tr ? 'Toplam' : 'Total', value: fmt(summary.users?.total) },
              { label: tr ? 'Aktif 24s' : 'Active 24h', value: fmt(summary.users?.activeToday) },
              { label: tr ? 'Aktif 7g' : 'Active 7d', value: fmt(summary.users?.active7d) },
              { label: tr ? 'Yeni 24s' : 'New 24h', value: fmt(summary.users?.created24h) },
              { label: tr ? 'Yeni 7g' : 'New 7d', value: fmt(summary.users?.created7d) },
              { label: tr ? 'Mail doğrulanmış' : 'Email verified', value: fmt(summary.users?.emailVerified) },
            ]} />
            <AdminCard accent="indigo" icon="🏠" title={tr ? 'Aileler' : 'Families'} stats={[
              { label: tr ? 'Toplam aile' : 'Total families', value: fmt(summary.families?.total) },
              { label: tr ? 'Aktif aile' : 'Active families', value: fmt(summary.families?.active) },
              { label: tr ? 'Boş aile' : 'Empty families', value: fmt(summary.families?.empty) },
              { label: tr ? 'Aktif üyelik' : 'Active memberships', value: fmt(summary.families?.memberships) },
              { label: tr ? 'Benzersiz üye' : 'Unique members', value: fmt(summary.families?.uniqueMembers) },
              { label: tr ? 'Yönetici kaydı' : 'Admin memberships', value: fmt(summary.families?.admins) },
              { label: tr ? 'Yeni 24s' : 'New 24h', value: fmt(summary.families?.created24h) },
              { label: tr ? 'Yeni 7g' : 'New 7d', value: fmt(summary.families?.created7d) },
              { label: tr ? 'Ortalama üye' : 'Avg members', value: summary.families?.avgMembers || 0 },
            ]} />
            <AdminCard accent="green" icon="🛒" title={tr ? 'Alışveriş' : 'Shopping'} stats={[
              { label: tr ? 'Toplam' : 'Total', value: fmt(summary.shopping?.total) },
              { label: tr ? 'Bekleyen' : 'Pending', value: fmt(summary.shopping?.pending) },
              { label: tr ? 'Tamamlanan' : 'Completed', value: fmt(summary.shopping?.completed) },
            ]} />
            <AdminCard accent="amber" icon="📅" title={tr ? 'Takvim' : 'Calendar'} stats={[
              { label: tr ? 'Toplam' : 'Total', value: fmt(summary.calendar?.total) },
              { label: tr ? 'Bugün' : 'Today', value: fmt(summary.calendar?.today) },
              { label: tr ? 'Yaklaşan' : 'Upcoming', value: fmt(summary.calendar?.upcoming) },
            ]} />
            <AdminCard accent="rose" icon="✅" title={tr ? 'Görevler' : 'Tasks'} stats={[
              { label: tr ? 'Toplam' : 'Total', value: fmt(summary.tasks?.total) },
              { label: tr ? 'Bekleyen' : 'Pending', value: fmt(summary.tasks?.pending) },
              { label: tr ? 'Tamamlanan' : 'Completed', value: fmt(summary.tasks?.completed) },
            ]} />
            <AdminCard accent="green" icon="💰" title={tr ? 'Finans' : 'Finance'} stats={[
              { label: tr ? 'Finans kaydı' : 'Finance records', value: fmt(summary.finance?.total) },
              { label: tr ? 'Gelir' : 'Income', value: fmt(summary.finance?.income) },
              { label: tr ? 'Gider' : 'Expense', value: fmt(summary.finance?.expense) },
              { label: tr ? 'Harcama' : 'Shared expense', value: fmt(summary.finance?.sharedExpenses) },
            ]} />
            <AdminCard accent="rose" icon="❤️" title={tr ? 'Sağlık' : 'Health'} stats={[
              { label: tr ? 'Toplam kayıt' : 'Total records', value: fmt(summary.health?.total) },
              { label: tr ? 'İlaç' : 'Medicines', value: fmt(summary.health?.medicines) },
              { label: tr ? 'Randevu' : 'Appointments', value: fmt(summary.health?.appointments) },
              { label: tr ? 'Aşı' : 'Vaccines', value: fmt(summary.health?.vaccines) },
              { label: tr ? 'Tahlil/Rapor' : 'Tests/Reports', value: fmt(summary.health?.tests) },
              { label: tr ? 'Dosya' : 'Files', value: fmt(summary.health?.files) },
              { label: tr ? 'Yaklaşan randevu' : 'Upcoming appts', value: fmt(summary.health?.upcomingAppointments) },
              { label: tr ? 'Aktif hatırlatma' : 'Active reminders', value: fmt(summary.health?.activeReminders) },
            ]} />
            <AdminCard accent="blue" icon="📍" title={tr ? 'Konum' : 'Location'} stats={[
              { label: tr ? 'Paylaşan' : 'Sharing', value: fmt(summary.location?.sharingUsers) },
              { label: tr ? '24s gönderen' : '24h senders', value: fmt(summary.location?.last24hSenders) },
              { label: tr ? 'Konum kaydı' : 'Location points', value: fmt(summary.location?.points) },
            ]} />
            <AdminCard accent="amber" icon="👶" title={tr ? 'Çocuk / Bebek / Evcil' : 'Children / Baby / Pet'} stats={[
              { label: tr ? 'Toplam profil' : 'Total profiles', value: fmt(summary.children?.total) },
              { label: tr ? 'Çocuk' : 'Children', value: fmt(summary.children?.children) },
              { label: tr ? 'Bebek' : 'Babies', value: fmt(summary.children?.babies) },
              { label: tr ? 'Evcil' : 'Pets', value: fmt(summary.children?.pets) },
              { label: tr ? 'Takip kaydı' : 'Tracking records', value: fmt(summary.children?.trackingRecords) },
            ]} />
            <AdminCard accent="indigo" icon="💬" title={tr ? 'Chat & Medya' : 'Chat & Media'} stats={[
              { label: tr ? 'Oda' : 'Rooms', value: fmt(summary.chat?.rooms), trendKey: 'chat.rooms' },
              { label: tr ? 'Mesaj' : 'Messages', value: fmt(summary.chat?.messages), trendKey: 'chat.messages' },
              { label: tr ? 'Medya' : 'Media', value: fmt(summary.chat?.media), trendKey: 'chat.media' },
              { label: tr ? 'Fotoğraf' : 'Photos', value: fmt(summary.chat?.photos), trendKey: 'chat.photos' },
              { label: tr ? 'Video' : 'Videos', value: fmt(summary.chat?.videos), trendKey: 'chat.videos' },
              { label: tr ? 'Ses' : 'Audio', value: fmt(summary.chat?.audios), trendKey: 'chat.audios' },
              { label: tr ? 'Boyut' : 'Size', value: bytes(summary.chat?.mediaBytes), trendKey: 'chat.mediaBytes' },
            ]} />
            <AdminCard accent="blue" icon="📄" title={tr ? 'Belgeler' : 'Documents'} stats={[
              { label: tr ? 'Belge' : 'Documents', value: fmt(summary.documents?.total) },
              { label: tr ? 'Boyut' : 'Size', value: bytes(summary.documents?.bytes) },
            ]} />
            <AdminCard accent="rose" icon="🔔" title={tr ? 'Bildirimler' : 'Notifications'} stats={[
              { label: tr ? 'Toplam' : 'Total', value: fmt(summary.notifications?.total) },
              { label: tr ? 'Başarılı' : 'Success', value: fmt(summary.notifications?.success) },
              { label: tr ? 'Başarısız' : 'Failed', value: fmt(summary.notifications?.failed) },
            ]} />

          </div>
          <EngagementStatsPanel tr={tr} engagement={summary.engagement || {}} />
          </>
        )}
      </section>

      {loading && <div className="adminLoadingOverlay">{txt.loading}</div>}
      </div>
    </AdminTrendRefreshContext.Provider>
  );
}
